import { ID, Permission, Query, Role } from "appwrite";
import {
	AppwriteClient,
	getAppwriteClient,
	getCurrentUser,
} from "$/modules/appwrite/lib/appwrite";
import { collections } from "$/modules/appwrite/lib/appwrite-collections";
import {
	type LiveAnswer,
	type LiveParticipationMode,
	type LiveQuestionPlanItem,
	type LiveQuestionResult,
	type LiveSelectionMode,
	type LiveSession,
	type LiveTeam,
	type Project,
	ProjectRole,
} from "$/modules/appwrite/types/collections";
import { executeExamAction } from "$/modules/exam/lib/exam";
import { getTranslations } from "$/modules/i18n/lib/i18n";
import { requireProjectRole } from "$/modules/projects/lib/project-access";
import { sortQuestionsByStoredOrder } from "$/modules/questionnaire/lib/question-order";

export type LiveTeamInput = {
	name: string;
	emoji?: string | null;
	color?: string | null;
};

export type LiveRoundOptions = {
	participationMode: LiveParticipationMode;
	selectionMode: LiveSelectionMode;
};

async function liveAction<T>(
	body: Record<string, unknown>,
	loadAsyncResult?: () => Promise<T>,
): Promise<T> {
	try {
		return await (loadAsyncResult
			? executeExamAction<T>(body, loadAsyncResult)
			: executeExamAction<T>(body));
	} catch (error) {
		if (error instanceof Error && error.message.startsWith("live.errors.")) {
			const t = await getTranslations("live");
			throw new Error(t(error.message.slice(5)));
		}
		throw error;
	}
}

export const getLiveQuestionPlan = (
	session: LiveSession,
): LiveQuestionPlanItem[] =>
	session.questionPlanJson ? JSON.parse(session.questionPlanJson) : [];

export const createLiveSession = async (
	project: Project,
	teams: LiveTeamInput[],
	options: LiveRoundOptions = {
		participationMode: "qr",
		selectionMode: "picker",
	},
) => {
	const t = await getTranslations("live");
	if (project.quizType !== "GameShow") throw new Error(t("gameshowOnly"));
	if (!(await getCurrentUser())) throw new Error(t("hostOnly"));
	await requireProjectRole(project, ProjectRole.Editor);
	const result = await liveAction<{
		success: true;
		session: LiveSession;
		teams: LiveTeam[];
	}>({
		action: "createLiveRound",
		projectId: project.$id,
		teams,
		...options,
		questionOrder: (project.questionnaires ?? []).map((category) => ({
			questionnaireId: category.$id,
			questionIds: sortQuestionsByStoredOrder(
				category.$id,
				category.questions ?? [],
			).map((question) => question.$id),
		})),
	});
	return { ...result, message: t("sessionCreated") };
};

export const changeLiveParticipationMode = (
	sessionId: string,
	participationMode: LiveParticipationMode,
) =>
	liveAction<LiveSession>({
		action: "changeLiveParticipationMode",
		sessionId,
		participationMode,
	});

export type LiveRoundSettings = {
	selectionMode: LiveSelectionMode;
	teams: (LiveTeamInput & { id?: string })[];
};
export type UpdatedLiveRound = { session: LiveSession; teams: LiveTeam[] };
export const updateLiveRoundSettings = (
	sessionId: string,
	settings: LiveRoundSettings,
) =>
	liveAction<UpdatedLiveRound>({
		action: "updateLiveRoundSettings",
		sessionId,
		...settings,
	});

export const openLivePicker = (sessionId: string) =>
	liveAction<LiveSession>({ action: "openLivePicker", sessionId });
export const selectLiveQuestion = (sessionId: string, questionId?: string) =>
	liveAction<LiveSession>({
		action: "selectLiveQuestion",
		sessionId,
		questionId,
	});
export const saveLiveRoundAnswer = (
	sessionId: string,
	questionId: string,
	teamId: string,
	answers: string[],
) =>
	liveAction<LiveAnswer>({
		action: "saveLiveAnswer",
		sessionId,
		questionId,
		teamId,
		answers,
	});
export const clearLiveRoundAnswer = (
	sessionId: string,
	questionId: string,
	teamId: string,
) =>
	liveAction({
		action: "saveLiveAnswer",
		sessionId,
		questionId,
		teamId,
		clear: true,
	});
export const revealLiveQuestion = (sessionId: string, questionId: string) =>
	liveAction<LiveQuestionResult>(
		{
			action: "revealLiveQuestion",
			sessionId,
			questionId,
		},
		async () => {
			const result = (await getLiveQuestionResults(sessionId)).find(
				(row) => row.questionId === questionId,
			);
			if (!result) {
				const t = await getTranslations("live");
				throw new Error(t("loadFailed"));
			}
			return result;
		},
	);
export const finishLiveRound = (sessionId: string) =>
	liveAction<LiveSession>({ action: "finishLiveRound", sessionId });

export const getLiveQuestionResults = async (sessionId: string) => {
	const rows: LiveQuestionResult[] = [];
	const appwrite = getAppwriteClient();
	while (true) {
		const page = await appwrite.tablesDB.listRows<LiveQuestionResult>(
			AppwriteClient.databaseId,
			collections.liveQuestionResults,
			[
				Query.equal("sessionId", sessionId),
				Query.limit(100),
				Query.offset(rows.length),
			],
		);
		rows.push(...page.rows);
		if (page.rows.length < 100) return rows;
	}
};

export const getLiveSession = (sessionId: string) => {
	const appwrite = getAppwriteClient();
	return appwrite.tablesDB.getRow<LiveSession>(
		AppwriteClient.databaseId,
		collections.liveSessions,
		sessionId,
	);
};

export const getLiveTeam = (teamId: string) => {
	const appwrite = getAppwriteClient();
	return appwrite.tablesDB.getRow<LiveTeam>(
		AppwriteClient.databaseId,
		collections.liveTeams,
		teamId,
	);
};

export const getLiveTeams = (sessionId: string) => {
	const appwrite = getAppwriteClient();
	return appwrite.tablesDB.listRows<LiveTeam>(
		AppwriteClient.databaseId,
		collections.liveTeams,
		[Query.equal("sessionId", sessionId), Query.limit(100)],
	);
};

export const updateLiveSession = async (
	session: LiveSession,
	data: Pick<
		LiveSession,
		"status" | "currentQuestionIndex" | "correctAnswersVisible"
	>,
) => {
	const appwrite = getAppwriteClient();
	return appwrite.tablesDB.updateRow(
		AppwriteClient.databaseId,
		collections.liveSessions,
		session.$id,
		data,
	);
};

export const submitLiveAnswer = async (
	sessionId: string,
	teamId: string,
	questionId: string,
	answers: string[],
) => {
	const appwrite = getAppwriteClient();
	return appwrite.tablesDB.createRow<LiveAnswer>(
		AppwriteClient.databaseId,
		collections.liveAnswers,
		ID.unique(),
		{ sessionId, teamId, questionId, answers },
		[
			Permission.read(Role.any()),
			Permission.update(Role.any()),
			Permission.delete(Role.any()),
		],
	);
};

export const getLiveAnswers = async (sessionId: string) => {
	const appwrite = getAppwriteClient();
	const rows: LiveAnswer[] = [];
	while (true) {
		const page = await appwrite.tablesDB.listRows<LiveAnswer>(
			AppwriteClient.databaseId,
			collections.liveAnswers,
			[
				Query.equal("sessionId", sessionId),
				Query.limit(100),
				Query.offset(rows.length),
			],
		);
		rows.push(...page.rows);
		if (page.rows.length < 100) return { rows };
	}
};
